/* search-more.js — <sf-search-more>, "Load more" on the search page (sections/main-search.liquid).

   The search page shows only results that pass the relevance gate (snippets/search-relevance.liquid). The gated
   list cannot be paged on the server — Liquid reads no query parameter but `page`, and `page` moves the window
   the gate scans — so main-search renders the first page of it plus this element, and the rest comes from
   sections/search-more.liquid: the same scan over the same URL, every gated result, fetched ONCE on the first
   click (nothing is fetched ahead of the visitor). Each click then appends the next page of it (data-step).

   Markup: <sf-search-more data-total data-shown data-step data-section data-str-showing data-str-loading data-str-error>
             <nav class="plp-more"> p[data-search-more-note][role=status] · button[data-search-more-btn][hidden] </nav>
           </sf-search-more>
   · The button is hidden in the markup and shown here, so without JavaScript the page shows the first page and
     the true count, never a dead button; a <noscript> "View all" button there submits the search to its show-all view.
   · The note is the live region; it is written in place, never replaced, so each change is announced.
   · A click hands focus to the first new product (as facets.js does); at the end the button goes and the note
     stays ("Showing 73 of 73").
   · facets.js re-renders [data-facets-results] on a filter or sort change, which replaces this element: the
     disconnected one aborts its request, and the new one starts fresh for the new list.
   · Appended cards upgrade like server-rendered ones (every card behaviour is a custom element); 'facets:more'
     tells <sf-facets> to re-measure its sticky header, exactly as its own load-more does.
   · The answer must be whole: its grid, and a <template data-search-more-total> count that matches the cards it
     holds. A cut-off or broken answer is an error (the note says so, the button stays for a retry); it never
     rewrites the count.
   · Never the same product twice: cards whose data-handle is already in the grid are skipped (as facets.js does),
     whatever order the second answer comes in. */
'use strict';
(function () {
  if (customElements.get('sf-search-more')) return;
  const theme = window.theme || {};

  customElements.define('sf-search-more', class extends HTMLElement {
    connectedCallback() {
      this._ctrl = new AbortController();
      this.btn = this.querySelector('[data-search-more-btn]');
      this.note = this.querySelector('[data-search-more-note]');
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.shown = parseInt(this.dataset.shown, 10) || 0;
      this.step = parseInt(this.dataset.step, 10) || 12;
      this.rest = null;
      this.extra = null;
      if (!this.btn) return;
      this.btn.addEventListener('click', (e) => { e.preventDefault(); this.more(); }, { signal: this._ctrl.signal });
      if (this.shown < this.total) this.btn.hidden = false;
      /* Shopify answered with more than 250 results: the page's gate saw only the first 250, so the matches in
         results 251–500 (window 2) are fetched now, added to the list and counted. */
      this._w2pending = this.hasAttribute('data-window2');
      if (this._w2pending) this.loadWindow2();
    }
    /* cards of one answer, refusing a cut-off one (its count must match its cards) */
    parseAnswer(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const g = doc.querySelector('[data-search-more-grid]');
      const tot = doc.querySelector('template[data-search-more-total]');
      const counted = tot ? parseInt(tot.getAttribute('data-search-more-total'), 10) : NaN;
      const all = g ? Array.from(g.children) : [];
      if (!g || isNaN(counted) || all.length !== counted) throw new Error('search-more: incomplete answer');
      return all;
    }
    knownHandles() {
      const seen = new Set();
      const grid = this.grid();
      if (grid) Array.from(grid.children).forEach((c) => { if (c.dataset && c.dataset.handle) seen.add(c.dataset.handle); });
      (this.rest || []).forEach((c) => { if (c.dataset && c.dataset.handle) seen.add(c.dataset.handle); });
      return seen;
    }
    async loadWindow2() {
      try {
        const u = new URL(this.restUrl());
        u.searchParams.set('page', '2');
        const res = await fetch(u.toString(), { signal: this._ctrl.signal, headers: { Accept: 'text/html' } });
        if (!res.ok || !this.isConnected) return;
        const cards = this.parseAnswer(await res.text());
        const seen = this.knownHandles();
        const add = cards.filter((c) => { const k = c.dataset && c.dataset.handle; if (!k || seen.has(k)) return false; seen.add(k); return true; });
        if (!add.length) return;
        if (this.rest !== null) this.rest = this.rest.concat(add);
        else this.extra = add;
        const before = this.total;
        this.total += add.length;
        this.writeNote();
        this.updateCount(before, this.total);
        if (this.btn && this.btn.isConnected) this.btn.hidden = false;
      } catch (err) { /* window 2 is a bonus: on failure the first 250 still stand, with their own true count */ } finally { this._w2pending = false; }
    }
    /* the toolbar's "N results for …" line counts the gated list: rewrite its number when window 2 adds to it */
    updateCount(from, to) {
      const root = this.closest('sf-facets') || document;
      root.querySelectorAll('[data-facets-count-wrap]').forEach((el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        for (let n = walker.nextNode(); n; n = walker.nextNode()) {
          if (n.nodeValue.indexOf(String(from)) > -1) { n.nodeValue = n.nodeValue.replace(String(from), String(to)); break; }
        }
      });
    }
    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      if (this._fetch) this._fetch.abort();
    }
    grid() {
      const root = this.closest('[data-facets-results]') || this.parentElement || document;
      return root.querySelector('[data-facets-grid]');
    }
    /* the list this page shows — the current address (facets.js keeps it in step with filters and sort) */
    restUrl() {
      const u = new URL(window.location.href);
      u.searchParams.delete('page');
      u.searchParams.delete('section_id');
      u.searchParams.set('section_id', this.dataset.section || 'search-more');
      return u.toString();
    }
    async load() {
      this._fetch = new AbortController();
      const res = await fetch(this.restUrl(), { signal: this._fetch.signal, headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('search-more ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const g = doc.querySelector('[data-search-more-grid]');
      const tot = doc.querySelector('template[data-search-more-total]');
      const counted = tot ? parseInt(tot.getAttribute('data-search-more-total'), 10) : NaN;
      const all = g ? Array.from(g.children) : [];
      if (!g || isNaN(counted) || all.length !== counted) throw new Error('search-more: incomplete answer');
      /* the same scan as the page, so normally the first `shown` are the cards already here: skip by handle */
      const key = (c) => (c.dataset && c.dataset.handle) || '';
      const seen = new Set();
      const grid = this.grid();
      if (grid) Array.from(grid.children).forEach((c) => { const k = key(c); if (k) seen.add(k); });
      this.rest = all.filter((c, i) => { const k = key(c); if (!k) return i >= this.shown; if (seen.has(k)) return false; seen.add(k); return true; });
      /* window 2's matches (results 251+), when they arrived first, go after window 1's */
      if (this.extra) { this.rest = this.rest.concat(this.extra.filter((c) => { const k = key(c); if (!k || seen.has(k)) return false; seen.add(k); return true; })); this.extra = null; }
      /* if the catalogue changed between the two requests, the second answer is the true count */
      const before = this.total;
      this.total = this.shown + this.rest.length;
      if (before !== this.total) this.updateCount(before, this.total);
    }
    async more() {
      if (this._busy) return;
      const btn = this.btn;
      this._busy = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      if (this.note && this.rest === null && this.dataset.strLoading) this.note.textContent = this.dataset.strLoading;
      try {
        if (this.rest === null) await this.load();
        if (!this.isConnected) return;
        const grid = this.grid();
        const batch = this.rest.splice(0, this.step);
        let first = null;
        if (grid && batch.length) {
          const frag = document.createDocumentFragment();
          batch.forEach((card) => frag.appendChild(document.adoptNode(card)));
          first = frag.firstElementChild;
          grid.appendChild(frag);
          this.shown += batch.length;
        }
        this.writeNote();
        if (!this.rest.length && this._w2pending) {
          btn.hidden = true; /* window 2 is still on its way; loadWindow2 shows the button again if it adds cards */
        } else if (!this.rest.length) {
          if (document.activeElement === btn && !first && this.note) { this.note.setAttribute('tabindex', '-1'); this.note.focus({ preventScroll: true }); }
          btn.remove();
        }
        if (grid && theme.initReveal) theme.initReveal(grid);
        if (grid && theme.initTitleMasks) theme.initTitleMasks(grid);
        if (first) {
          const target = first.querySelector('.card-b__link') || first.querySelector('a[href]:not([tabindex="-1"]), button');
          if (target) target.focus({ preventScroll: true });
        }
        this.dispatchEvent(new CustomEvent('facets:more', { bubbles: true, detail: { url: window.location.href } }));
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this.rest = null;
        if (this.note && this.dataset.strError) this.note.textContent = this.dataset.strError;
        else this.writeNote();
      } finally {
        this._busy = false;
        if (btn.isConnected) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
      }
    }
    writeNote() {
      if (!this.note) return;
      const tpl = this.dataset.strShowing || '[shown] / [total]';
      this.note.textContent = tpl.split('[shown]').join(String(this.shown)).split('[total]').join(String(this.total));
    }
  });
})();
